import React, { useEffect, useRef, useState } from 'react';

import { getTextWidth } from '../core/ArrayMultiselectFieldWithChipsFormik/utils';

export const useArrayText = ({ array, renderLabel, labelAdditionalWidth = 0, showOnly = false }) => {
  const containerRef = useRef(null);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [remainingCount, setRemainingCount] = useState(0);

  const getDefaultItem = React.useCallback(() => {
    return array?.length > 0 ? array[0] : null;
  }, [array]);

  useEffect(() => {
    if (!array || array?.length === 0 || !containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    // Read once rather than per item: `getComputedStyle` forces a style recalc, and the container's
    // font cannot change while we walk the list.
    const { font } = getComputedStyle(containerRef.current);
    let currentWidth = 0;
    const items = [];

    // Stop at the first item that does not fit rather than testing every remaining one. Callers pass
    // whole unbounded lists (a policy's covered objects), and each test costs a `measureText`; a loop
    // that keeps going measures the entire list on every layout pass once the row is nearly full,
    // because "no room for this 50px label" never becomes "no room for anything".
    // The displayed set is therefore always a prefix, which is what the `+N` remainder and the
    // tooltip both read it as. This drops the previous behaviour of skipping an over-wide label and
    // squeezing in a later narrower one — that packed a couple more chips in but rendered a
    // non-contiguous subset, so the row and the tooltip disagreed about *which* items were shown.
    // Matches the sibling measurement loop in core/Molecules/Fields/MultiSelectAutoComplete.
    for (let index = 0; index < array.length; index += 1) {
      const item = array[index];
      const label = renderLabel(item);
      const labelWidth = getTextWidth(label + (index !== array.length - 1 ? ', ' : ''), font);

      const fits = currentWidth + labelWidth + labelAdditionalWidth + 6 <= containerWidth;
      // `showOnly` still guarantees a first label even when it overflows on its own.
      if (!fits && !(showOnly && index === 0)) break;

      items.push(item);
      currentWidth += labelWidth + labelAdditionalWidth;
    }

    if (items.length === 0) items.push(getDefaultItem());

    setDisplayedItems(items);
    setRemainingCount(array.length - items.length);
  }, [array, getDefaultItem, labelAdditionalWidth, renderLabel, showOnly]);

  return {
    containerRef,
    displayedItems,
    remainingCount,
  };
};
